import { Component } from '@angular/core';
import { Role } from '../../models/Role';
import { AffectedUsersResponse } from '../../models/AffectedUsersResponse';
import { ModalService } from '../../services/modal.service';
import { AuthService } from '../../services/auth.service';
import { FollowUpService } from '../../services/follow-up.service';
import { ModulesService } from '../../services/modules.service';
import { firstValueFrom } from 'rxjs';
import { LastValidated } from '../../models/LastValidated';
import { User } from '../../models/User';
import { FollowUp } from '../../models/FollowUpResponse';
import { Module } from '../../models/Module';
import { AddAccountModalComponent } from '../modales/add-account-modal/add-account-modal.component';
import { ChildAddedModaleComponent } from '../modales/child-added-modale/child-added-modale.component';

import { TranslateModule } from '@ngx-translate/core';
import { ChildDetailsModaleComponent } from '../modales/child-details-modale/child-details-modale.component';

@Component({
    selector: 'app-follow-up-parent',
    standalone: true,
    imports: [TranslateModule],
    templateUrl: './follow-up-parent.component.html',
    styleUrl: './follow-up-parent.component.scss',
})
export class FollowUpParentComponent {
    user?: User;
    affectedUsers: AffectedUsersResponse | null = null;
    followup: FollowUp[];
    modules: Module[];
    dates: { [userId: string]: LastValidated } = {};
    Role = Role;

    constructor(
        private modalService: ModalService,
        private authService: AuthService,
        private followUpService: FollowUpService,
        private moduleService: ModulesService
    ) {}

    async ngOnInit() {
        await this.fetchData();
    }

    async fetchData() {
        const user = await firstValueFrom(
            this.authService.getUserInfoOrRefreshIfNeeded()
        );
        const res = await firstValueFrom(
            this.followUpService.getStudentsFollowUp()
        );
        const modules = await firstValueFrom(
            this.moduleService.getAllModules()
        );

        if (user.is_parent) {
            const affectedUsers = await firstValueFrom(
                this.authService.getAffectedUsers()
            );
            this.affectedUsers = affectedUsers;
        }

        this.user = user;
        this.modules = modules;
        this.followup = res.data;
        this.computeLastDateForUsers();
    }

    computeLastDateForUsers() {
        this.followup.forEach((fu) => {
            const userId = fu.user_id;
            const array = fu.progresses
                .filter((elt) => elt.last_validation_datetime !== null)
                .map((elt) => elt.last_validation_datetime)
                .map((elt) => new Date(elt))
                .sort((a, b) => b.getTime() - a.getTime());

            if (array.length === 0) {
                this.dates[userId] = new LastValidated(undefined, undefined);
            } else {
                const date = array[0];
                const stepDay = Math.floor(
                    (new Date().getTime() - array[0].getTime()) /
                        (1000 * 3600 * 24)
                );
                this.dates[userId] = new LastValidated(date, stepDay);
            }
        });
    }

    addParent() {
        this.openAddAccountModale(Role.parent);
    }

    addChild() {
        this.openAddAccountModale(Role.enfant);
    }

    openAddAccountModale(role: Role) {
        if (this.shouldDisableAddButton(role)) {
            return;
        }

        const modalRef = this.modalService.open(AddAccountModalComponent, {
            size: 'lg',
        });
        modalRef.componentInstance.role = role;
        modalRef.result.then(async (result) => {
            if (result.message === 'created') {
                const pseudo = result.pseudo;

                await this.fetchData();
                if (!pseudo) {
                    return;
                }

                const user = this.affectedUsers?.children.find(
                    (elt) => elt.pseudo === pseudo
                );
                if (!user) {
                    return;
                }

                const modal = this.modalService.open(
                    ChildAddedModaleComponent,
                    {
                        size: 'lg',
                    }
                );
                modal.componentInstance.user = user;
            }
        });
    }

    shouldDisableAddButton(role: Role): boolean {
        if (this.affectedUsers === null) {
            return true;
        }

        if (role === Role.enfant) {
            return (
                this.affectedUsers.children.length >=
                this.affectedUsers.max_children
            );
        } else {
            return (
                this.affectedUsers.adults.length >=
                this.affectedUsers.max_adults
            );
        }
    }

    getNameFor(followUp: FollowUp): string {
        return `${followUp.user_infos.first_name} ${followUp.user_infos.last_name}`;
    }

    displayDetailsFor(followUp: FollowUp) {
        const modal = this.modalService.open(ChildDetailsModaleComponent, {
            size: 'lg',
            scrollable: true,
        });
        modal.componentInstance.followUp = followUp;
        modal.componentInstance.dates = this.dates;
        modal.componentInstance.modules = this.modules;
    }
}
